import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Check, Store } from "lucide-react";
import { useAgencyWorkspace } from "@/context/AgencyWorkspaceContext";

function Avatar({ tenant }) {
  const initial = (tenant?.name || "?").trim().charAt(0).toUpperCase();
  if (tenant?.logo) {
    return <img src={tenant.logo} alt="" className="h-6 w-6 rounded-md object-cover flex-shrink-0" />;
  }
  return (
    <span className="h-6 w-6 rounded-md bg-[color:var(--sp-brand)]/15 text-[color:var(--sp-brand)] text-[11px] font-black flex items-center justify-center flex-shrink-0">
      {initial}
    </span>
  );
}

export function TenantSwitcher() {
  const { availableTenants, selectedTenant, setSelectedTenant, loading } = useAgencyWorkspace();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return availableTenants;
    return availableTenants.filter((t) => (t.name || "").toLowerCase().includes(s));
  }, [q, availableTenants]);

  if (loading) {
    return <div data-testid="tenant-switcher-loading" className="h-10 w-56 rounded-full bg-[color:var(--sp-subtle)] animate-pulse" />;
  }
  if (!availableTenants.length) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="tenant-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2.5 pl-3 pr-4 h-10 rounded-full bg-[color:var(--sp-subtle)] border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]/40 hover:bg-white transition-colors max-w-[300px]">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] hidden sm:inline">Salone</span>
        {selectedTenant ? <Avatar tenant={selectedTenant} /> : <Store className="h-5 w-5 text-[color:var(--sp-text-muted)]" />}
        <span data-testid="tenant-switcher-current" className="font-bold text-sm truncate text-[color:var(--sp-text)]">
          {selectedTenant?.name || "Seleziona salone"}
        </span>
        <ChevronDown className="h-4 w-4 text-[color:var(--sp-text-muted)] flex-shrink-0" />
      </button>
      {open && (
        <div data-testid="tenant-switcher-menu"
          className="absolute z-50 mt-2 left-0 w-[320px] rounded-2xl border border-[color:var(--sp-border)] bg-white shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-[color:var(--sp-border)]">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca salone…" data-testid="tenant-switcher-search"
                className="sp-input !pl-10 !py-1.5 text-sm w-full" />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="p-4 text-xs text-[color:var(--sp-text-muted)] text-center italic">Nessun salone</div>
            ) : filtered.map((t) => (
              <button key={t.id} type="button"
                data-testid={`tenant-option-${t.id}`}
                onClick={() => { setSelectedTenant(t.id); setOpen(false); setQ(""); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left hover:bg-[color:var(--sp-subtle)] ${selectedTenant?.id === t.id ? "bg-[color:var(--sp-brand)]/5" : ""}`}>
                <Avatar tenant={t} />
                <span className="flex-1 text-sm font-semibold truncate">{t.name}</span>
                {selectedTenant?.id === t.id && <Check className="h-4 w-4 text-[color:var(--sp-brand)]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
